import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';

import { LoggingService } from '@data-terminal/base/app/services/logging.service';
import { APP_RUNTIME, AppRuntime } from '@data-terminal/data-access/injection-tokens';
import { InactivityTimerComponent } from '@data-terminal/feature-inactivity-tracker/components/inactivity-timer/inactivity-timer.component';
import { InactivityTrackerService } from '@data-terminal/feature-inactivity-tracker/services';
import { ROUTE_PATH } from '@data-terminal/shared-models/navigation';
import { TimeFormatterPipeModule } from '@data-terminal/utils/pipes';
import { CCAuthService } from '@heidelberg/control-center-frontend-integration/auth';
import { TranslateModule } from '@ngx-translate/core';

export interface InactivityMachineEntry {
    machineId: string;
    timer$: Observable<number> | undefined;
}

export interface InactivityTimerDialogData {
    entries$: Observable<InactivityMachineEntry[]>;
    onClear: () => void;
}

@Component({
    selector: 'data-terminal-inactivity-timer-dialog',
    templateUrl: './inactivity-timer-dialog.component.html',
    styleUrls: ['./inactivity-timer-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AsyncPipe,
        MatButtonModule,
        MatIconModule,
        MatToolbarModule,
        InactivityTimerComponent,
        TimeFormatterPipeModule,
        TranslateModule,
    ],
})
export class InactivityTimerDialogComponent {
    readonly #dialogRef = inject<MatDialogRef<InactivityTimerDialogComponent>>(MatDialogRef);
    readonly #router = inject(Router);
    readonly #ccAuthService = inject(CCAuthService);
    readonly #inactivityTrackerService = inject(InactivityTrackerService);
    readonly #loggingService = inject(LoggingService);
    readonly #appRuntime = inject<AppRuntime>(APP_RUNTIME);
    public readonly data = inject<InactivityTimerDialogData>(MAT_DIALOG_DATA);

    // Latest snapshot of shown machines, used to reset all of them when the
    // whole dialog is closed at once (header close / navigate).
    private currentEntries: InactivityMachineEntry[] = [];

    constructor() {
        this.data.entries$.pipe(takeUntilDestroyed()).subscribe((entries) => (this.currentEntries = entries));
    }

    public onNavigate(machineId: string): void {
        const isOnlinePressWebComponent = this.#appRuntime === AppRuntime.ONLINE_PRESS_WEB_COMPONENT;
        const orgId = this.#ccAuthService.getCurrentMembership()?.organizationId;

        this.resetAllAndClose();

        if (!orgId) {
            return;
        }

        this.#router.navigate([
            orgId,
            isOnlinePressWebComponent ? ROUTE_PATH.onlinePress : ROUTE_PATH.dashboard,
            machineId,
            ROUTE_PATH.operations,
        ]);
    }

    public onCloseAll(): void {
        this.resetAllAndClose();
    }

    private resetAllAndClose(): void {
        this.currentEntries.forEach((entry) => this.resetInactivityTracking(entry.machineId));
        this.data.onClear();
        this.#dialogRef.close();
    }

    private resetInactivityTracking(machineId: string): void {
        // Guard against updateTimerOffset throwing (e.g. the machine was
        // unassigned while the dialog was open) so that a single failing entry
        // never blocks resetting the others or closing the (disableClose) dialog.
        try {
            this.#inactivityTrackerService.updateTimerOffset(machineId);
            this.#inactivityTrackerService.markMachineUnchecked(machineId);
        } catch (error) {
            this.#loggingService.error('Failed to reset inactivity tracking', { payload: { machineId, error } });
        }
    }
}
